/**
 * Converts temperature from Celsius to Fahrenheit if needed based on unit preference
 * @param {number} value - The temperature value to convert
 * @param {string} unitType - The user's preferred unit type ('Metric' or 'Imperial')
 * @returns {number} - The converted temperature value
 */
export const convertTemperature = (value, unitType) => {
    if (!value || typeof value !== 'number') return value;

    try {
        if (unitType === 'Imperial') {
            return (value * 9 / 5) + 32;
        }
        return value;
    } catch (error) {
        console.error('Error converting temperature:', error);
        return value;
    }
};

/**
 * Converts weight from kilograms to pounds if needed based on unit preference
 * @param {number} value - The weight value to convert
 * @param {string} unitType - The user's preferred unit type ('Metric' or 'Imperial')
 * @returns {number} - The converted weight value
 */
export const convertWeight = (value, unitType) => {
    if (!value || typeof value !== 'number') return value;

    try {
        if (unitType === 'Imperial') {
            return value * 2.20462;
        }
        return value;
    } catch (error) {
        console.error('Error converting weight:', error);
        return value;
    }
};

/**
 * Converts measurement values based on modality and unit preferences
 * @param {Object} reading - The reading object containing measurement data
 * @param {string} unitType - The user's preferred unit type ('Metric' or 'Imperial')
 * @returns {Object} - The reading object with converted measurements
 */
export const convertMeasurementUnits = (reading, unitType) => {
    if (!reading || !reading.modalityId) return reading;

    const converted = { ...reading };

    try {
        switch (reading.modalityId) {
            case 1: // Temperature
                if (reading.measurement !== null) {
                    converted.measurement = convertTemperature(reading.measurement, unitType);
                    if (reading.groupMaxVal !== undefined) {
                        converted.groupMaxVal = convertTemperature(reading.groupMaxVal, unitType);
                        converted.groupMinVal = convertTemperature(reading.groupMinVal, unitType);
                    }
                }
                break;

            case 8: // Weight
                if (reading.measurement !== null) {
                    converted.measurement = convertWeight(reading.measurement, unitType);
                    if (reading.groupMaxVal !== undefined) {
                        converted.groupMaxVal = convertWeight(reading.groupMaxVal, unitType);
                        converted.groupMinVal = convertWeight(reading.groupMinVal, unitType);
                    }
                }
                break;

            default:
                break;
        }

        return converted;
    } catch (error) {
        console.error('Error converting measurement units:', error);
        return reading;
    }
};

/**
 * Converts measurement values based on modality and unit preferences
 * @param {Object} reading - The reading object containing measurement data
 * @param {string} unitType - The user's preferred unit type ('Metric' or 'Imperial')
 * @returns {Object} - The reading object with converted measurements
 */
export const convertMeasurementValue = (value, modalityId, unitType) => {
    if (!value) return value;

    try {
        switch (modalityId) {
            case 1: // Temperature
                if (value !== null) {
                    return convertTemperature(value, unitType);
                }
            case 8: // Weight
                if (value !== null) {
                    return convertWeight(value, unitType);
                }

            default:
                return value;
        }
    } catch (error) {
        console.error('Error converting measurement units:', error);
        return value;
    }
};

/**
 * Converts measurement values based on modality and unit preferences
 * @param {Object} reading - The reading object containing measurement data
 * @param {string} unitType - The user's preferred unit type ('Metric' or 'Imperial')
 * @returns {Object} - The reading object with converted measurements
 */
export const convertAndFormatMeasurementValue = (value, modalityId, unitType) => {
    if (value === null || value === undefined) return value;
    let convertedValue = convertMeasurementValue(value, modalityId, unitType);
    let formattedValue = formatValueByModalityId(convertedValue, modalityId);
    return `${formattedValue}${getVitalUnit(modalityId, unitType)}`;
};



export const getVitalUnit = (modalityId, userUnitPreference) => {
    switch (modalityId) {
        case 1: // Temperature
            return userUnitPreference === 'Imperial' ? '°F' : '°C';
        case 2: // pulse rate
            return ' bpm';
        case 3: // SpO2
            return '%';
        case 4: // Heart Rate
            return ' bpm';
        case 5: // Systolic
            return ' mmHg';
        case 6: // Respiratory Rate
            return ' br/min';
        case 8: // Weight
            return userUnitPreference === 'Imperial' ? ' lb' : ' kg';
        case 9: // Height
            return userUnitPreference === 'Imperial' ? ' in' : ' cm';
        case 10: // Diastolic
            return ' mmHg';
        case 11: // glucose
            return ' mmol/L';
        case 13: // steps
            return ' steps';
        case 15: // heart rate variability
            return '%';
        case 16: // arrthymia
            return '%';
        default:
            return '';
    }
};

export const getUnitByVitalType = (vitalType, userPreference) => {
    switch (vitalType) {
        case 'temperature':
            return userPreference === 'Imperial' ? '°F' : '°C';
        case 'weight':
            return userPreference === 'Imperial' ? ' lb' : ' kg';
        case 'oxygenSaturation':
        case 'ECGHRV':
            return '%';
        case 'ECG':
            return ' bpm';
        case 'respiratoryRate':
            return ' br/min';
        case 'systolic':
        case 'diastolic':
            return ' mmHg';
        case 'AUSC':
            return ' Hz';
        case 'steps':
            return ' Steps';
        case 'glucoseLevel':
            return ' mg/dL';
        default:
            return '';
    }
};


/**
 * Converts measurement values based on modality and unit preferences
 * @param {Object} reading - The reading object containing measurement data
 * @param {string} unitType - The user's preferred unit type ('Metric' or 'Imperial')
 * @returns {Object} - The reading object with converted measurements
 */
export const formatValueByModalityId = (value, modalityId) => {
    if (!value || isNaN(value)) return value;

    try {
        switch (modalityId) {
            case 1:
                return Number(parseFloat(value).toFixed(1));
            default:
                return Math.round(value);
        }
    } catch (error) {
        console.error('Error formatting measurement units:', error);
        return value;
    }
};

/**
 * Gets the latest measurement or average from health evaluation data
 * @param {Array} healthEvaluationData - Array of health evaluation entries
 * @param {string} vitalName - The name of the vital to get measurement for
 * @param {boolean} averageGrouped - Whether to calculate average or get latest
 * @param {string} unit - The unit string to append (e.g., '°F', '°C', ' bpm', '%')
 * @param {Object} options - Optional configuration
 * @param {string} options.filterField - Field name to filter by (defaults to vitalName)
 * @param {string} options.valueField - Field name to extract value from (defaults to vitalName)
 * @param {number} options.decimalPlaces - Number of decimal places (defaults to 0, rounds if not specified)
 * @returns {Object} - Object with measurement string and latestDate string
 */
export const getLatestMeasurement = (
    healthEvaluationData,
    vitalName,
    averageGrouped,
    userUnitPreference,
    vitals,
    decimalPlaces,
    systemRules = []
) => {

    if (!healthEvaluationData || healthEvaluationData.length === 0) {
        return { measurement: 'No Data', latestDate: 'N/A' };
    }
    
    const unit = getVitalUnit(vitals?.modalityId, userUnitPreference);

    // Filter entries where the filter field is not null
    const deviceNamesData = healthEvaluationData.filter(entry => entry[vitalName] !== null);

    if (averageGrouped) {
        // Calculate average for grouped intervals
        const validEntries = deviceNamesData.filter(entry => 
            entry[vitalName] !== null && !isNaN(entry[vitalName])
        );

        if (validEntries.length === 0) {
            return {
                measurement: 'No Data',
                latestDate: 'Interval Average',
            };
        }

        const total = validEntries.reduce((sum, entry) => sum + entry[vitalName], 0);
        const average = total / validEntries.length;

        // Calculate out of threshold count
        let outOfRangeCount = 0;
        if ((vitals?.upperLimit !== undefined && vitals?.upperLimit !== 0) || (vitals?.lowerLimit !== undefined && vitals?.lowerLimit !== 0)) {
            const outOfRange = validEntries.filter((entry) => {
                const value = entry[vitalName];
                if (value === null || value === undefined) {
                    return false;
                }

                const convertedUpperLimit = convertMeasurementValue(vitals?.upperLimit, vitals?.modalityId, userUnitPreference);
                const convertedLowerLimit = convertMeasurementValue(vitals?.lowerLimit, vitals?.modalityId, userUnitPreference);
                if (value > convertedUpperLimit || value < convertedLowerLimit) {
                    return true;
                }
                return false;
            });
            outOfRangeCount = outOfRange.length;
        }

        // Format the value
        let formattedValue;
        if (decimalPlaces !== null) {
            formattedValue = average.toFixed(decimalPlaces);
        } else {
            formattedValue = Math.round(average);
        }

        

        // Build latestDate string with threshold count if applicable
        let latestDate = 'Interval Average';
        if (outOfRangeCount > 0) {
            latestDate = `${outOfRangeCount} out of threshold`;
        }

        if (vitals?.modalityId === 6) {
            let defaultRespValues = systemRules.find((item)=>item.modalityId === vitals?.modalityId && item.type === 'modality');
            if (defaultRespValues && formattedValue > defaultRespValues.targetUpperValue) {
                return {
                    measurement: `> 30 br/min`,
                    latestDate: latestDate,
                };
            }
        }

        return {
            measurement: `${formattedValue}${unit}`,
            latestDate: latestDate,
        };
    } else {
        // Get the latest measurement by date
        if (deviceNamesData.length === 0) {
            return { measurement: 'No Data', latestDate: 'N/A' };
        }

        let outOfRangeCount = 0;
        if ((vitals?.upperLimit !== undefined && vitals?.upperLimit !== 0) || (vitals?.lowerLimit !== undefined && vitals?.lowerLimit !== 0)) {
            const outOfRange = deviceNamesData.filter((entry) => {
                const value = entry[vitalName];
                if (value === null || value === undefined) {
                    return false;
                }

                const convertedUpperLimit = convertMeasurementValue(vitals?.upperLimit, vitals?.modalityId, userUnitPreference);
                const convertedLowerLimit = convertMeasurementValue(vitals?.lowerLimit, vitals?.modalityId, userUnitPreference);
                if (value > convertedUpperLimit || value < convertedLowerLimit) {
                    return true;
                }
            });
            outOfRangeCount = outOfRange.length;
        }
        const latestMeasurement = deviceNamesData.reduce((latest, entry) => {
            const dateA = new Date(latest.date);
            const dateB = new Date(entry.date);
            return dateB > dateA ? entry : latest;
        }, deviceNamesData[0]);

        if (!latestMeasurement) {
            return { measurement: 'No Data', latestDate: 'N/A' };
        }

        const value = latestMeasurement[vitalName];
        if (value === null || value === undefined) {
            return { measurement: 'No Data', latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A') };
        }

        // Format the value
        let formattedValue;
        if (decimalPlaces !== null) {
            formattedValue = value.toFixed(decimalPlaces);
        } else {
            formattedValue = Math.round(value);
        }
        if (vitals?.modalityId === 6) {
            let defaultRespValues = systemRules.find((item)=>item.modalityId === vitals?.modalityId && item.type === 'modality');
            if (defaultRespValues && formattedValue > defaultRespValues.targetUpperValue) {
                return {
                    measurement: `> 30 br/min`,
                    latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A'),
                    outOfRangeCount: outOfRangeCount
                };
            }

        }

        return {
            measurement: `${formattedValue}${unit}`,
            latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A'),
            outOfRangeCount: outOfRangeCount
        };
    }
};